import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, resolvePhoto } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft, Users, AlertTriangle, ShieldCheck, CreditCard, Search,
  Ban, RotateCcw, Check, X, Loader2, Eye, TrendingUp, Activity,
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: TrendingUp },
  { id: "users", label: "Users", icon: Users },
  { id: "reports", label: "Reports", icon: AlertTriangle },
  { id: "verifications", label: "Verifications", icon: ShieldCheck },
  { id: "transactions", label: "Revenue", icon: CreditCard },
];

export default function AdminPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  if (loading) return null;
  if (!user?.is_admin) {
    return (
      <div className="min-h-screen grain-bg flex items-center justify-center p-6 text-center" data-testid="admin-forbidden">
        <div>
          <Ban size={40} className="text-[#D9534F] mx-auto" strokeWidth={1.5} />
          <h1 className="font-serif text-4xl mt-4 text-[#1A1818]">Admins only.</h1>
          <p className="text-[#737373] mt-2 max-w-sm">
            Add your email to <span className="font-mono text-xs bg-[#F0EBE1] px-1.5 py-0.5 rounded">ADMIN_EMAILS</span> in <span className="font-mono text-xs">backend/.env</span> to manage Rendezvous.
          </p>
          <button onClick={() => navigate("/app")} data-testid="admin-back-to-app"
            className="mt-6 bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full px-6 py-2.5">
            Back to app
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain-bg" data-testid="admin-page">
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between border-b border-[#E5DFD3] bg-[#FAF7F2]/80 backdrop-blur sticky top-0 z-30">
        <button onClick={() => navigate("/app")} data-testid="admin-back"
          className="flex items-center gap-2 text-[#737373] hover:text-[#1A1818]">
          <ArrowLeft size={16} /> Back to app
        </button>
        <div className="text-center">
          <div className="font-serif italic text-2xl text-[#913831]">rendezvous</div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#737373] mt-0.5">Admin Console</div>
        </div>
        <div className="text-xs text-[#737373]">Signed in as <span className="font-medium text-[#1A1818]">{user.email}</span></div>
      </header>

      <div className="flex flex-col lg:flex-row max-w-7xl mx-auto">
        <aside className="lg:w-56 lg:border-r lg:border-[#E5DFD3] p-4 lg:p-6">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto" data-testid="admin-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
                    tab === t.id ? "bg-[#1A1818] text-white" : "text-[#1A1818] hover:bg-[#F0EBE1]"
                  }`}>
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 p-6 lg:p-10">
          {tab === "overview" && <OverviewTab />}
          {tab === "users" && <UsersTab />}
          {tab === "reports" && <ReportsTab />}
          {tab === "verifications" && <VerificationsTab />}
          {tab === "transactions" && <TransactionsTab />}
        </main>
      </div>
    </div>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
  }, []);
  if (!stats) return <Loader2 className="animate-spin" />;
  const tiles = [
    { label: "Users", value: stats.users_total, sub: `${stats.users_onboarded} onboarded`, accent: false },
    { label: "Verified", value: stats.users_verified, sub: `${Math.round(100 * stats.users_verified / Math.max(stats.users_total,1))}% of base`, accent: false },
    { label: "Quiet+ active", value: stats.active_subscriptions, sub: "current subscribers", accent: true },
    { label: "Revenue (USD)", value: `$${stats.revenue_usd}`, sub: "all-time, gross", accent: true },
    { label: "Matches", value: stats.matches_total, sub: `${stats.messages_total} messages exchanged`, accent: false },
    { label: "Open reports", value: stats.reports_open, sub: "needs review", accent: stats.reports_open > 0 },
  ];
  return (
    <section data-testid="admin-overview">
      <h2 className="font-serif text-4xl tracking-tight text-[#1A1818]">Overview.</h2>
      <p className="text-sm text-[#737373] mt-1">Generated {new Date(stats.generated_at).toLocaleString()}</p>
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4">
        {tiles.map((t, i) => (
          <motion.div key={t.label} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            data-testid={`stat-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}
            className={`rounded-3xl p-6 border ${
              t.accent ? "bg-[#1A1818] text-white border-[#1A1818]" : "bg-white text-[#1A1818] border-[#E5DFD3]"
            }`}>
            <div className={`text-xs uppercase tracking-[0.2em] ${t.accent ? "text-[#D4A373]" : "text-[#737373]"}`}>{t.label}</div>
            <div className="font-serif text-5xl mt-2">{t.value}</div>
            <div className={`text-xs mt-1 ${t.accent ? "text-white/70" : "text-[#737373]"}`}>{t.sub}</div>
          </motion.div>
        ))}
      </div>
      <div className="mt-10 bg-white border border-[#E5DFD3] rounded-3xl p-6 flex items-start gap-4" data-testid="admin-tip">
        <Activity size={18} className="text-[#913831] mt-0.5" />
        <div className="text-sm text-[#1A1818]">
          <div className="font-medium">You are an admin via env var.</div>
          <div className="text-[#737373] mt-1">To add another admin, edit <span className="font-mono text-xs bg-[#F0EBE1] px-1.5 py-0.5 rounded">ADMIN_EMAILS</span> in <span className="font-mono text-xs">backend/.env</span> (comma-separated) and restart the backend.</div>
        </div>
      </div>
    </section>
  );
}

function UsersTab() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users?limit=100${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setUsers(data);
    } catch {
      toast.error("Couldn't load users");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const act = async (uid, action) => {
    try {
      await api.post(`/admin/users/${uid}/${action}`);
      toast.success(`User ${action}ed`);
      load();
    } catch {
      toast.error("Action failed");
    }
  };

  return (
    <section data-testid="admin-users">
      <h2 className="font-serif text-4xl tracking-tight text-[#1A1818]">Users.</h2>
      <form className="mt-5 relative max-w-md" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#737373]" />
        <input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full bg-white border border-[#E5DFD3] rounded-full pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#913831]" />
      </form>

      {loading ? <Loader2 className="mt-8 animate-spin" /> : (
        <div className="mt-6 bg-white border border-[#E5DFD3] rounded-3xl overflow-hidden" data-testid="users-table">
          <div className="grid grid-cols-12 gap-2 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#737373] border-b border-[#E5DFD3]">
            <div className="col-span-5">User</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Created</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`}
              className="grid grid-cols-12 gap-2 px-5 py-3 items-center border-b border-[#E5DFD3]/60 hover:bg-[#F0EBE1]/40">
              <div className="col-span-5 flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full overflow-hidden bg-[#F0EBE1] flex-shrink-0">
                  {u.photos?.[0] ? <img src={resolvePhoto(u.photos[0])} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#1A1818] truncate flex items-center gap-1.5">
                    {u.name || "—"} {u.verified && <ShieldCheck size={11} className="text-[#913831]" />}
                    {u.is_admin && <span className="text-[9px] uppercase tracking-[0.15em] bg-[#1A1818] text-white px-1.5 py-0.5 rounded-full">admin</span>}
                  </div>
                  <div className="text-xs text-[#737373] truncate">{u.email}</div>
                </div>
              </div>
              <div className="col-span-2 text-xs">
                {u.suspended
                  ? <span className="text-[#D9534F]">Suspended</span>
                  : u.onboarded
                  ? <span className="text-[#4caf50]">Active</span>
                  : <span className="text-[#737373]">Onboarding</span>}
              </div>
              <div className="col-span-2 text-xs text-[#737373]">
                {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
              </div>
              <div className="col-span-3 flex gap-1.5 justify-end flex-wrap">
                {!u.verified && (
                  <button data-testid={`act-verify-${u.id}`} onClick={() => act(u.id, "verify")}
                    title="Mark as verified"
                    className="text-xs px-2.5 py-1 rounded-full border border-[#E5DFD3] hover:bg-[#F0EBE1] text-[#913831]"><Check size={12} className="inline" /> Verify</button>
                )}
                {u.suspended ? (
                  <button data-testid={`act-unsuspend-${u.id}`} onClick={() => act(u.id, "unsuspend")}
                    className="text-xs px-2.5 py-1 rounded-full border border-[#E5DFD3] hover:bg-[#F0EBE1] text-[#4caf50]"><RotateCcw size={12} className="inline" /> Restore</button>
                ) : (
                  <button data-testid={`act-suspend-${u.id}`} onClick={() => act(u.id, "suspend")}
                    className="text-xs px-2.5 py-1 rounded-full border border-[#D9534F]/40 text-[#D9534F] hover:bg-[#FFF4F1]"><Ban size={12} className="inline" /> Suspend</button>
                )}
              </div>
            </div>
          ))}
          {users.length === 0 && (
            <div className="p-10 text-center text-[#737373] text-sm" data-testid="users-empty">No users match.</div>
          )}
        </div>
      )}
    </section>
  );
}

function ReportsTab() {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reports?status_filter=${filter}`);
      setReports(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const resolve = async (id) => {
    try { await api.post(`/admin/reports/${id}/resolve`); toast.success("Resolved"); load(); }
    catch { toast.error("Action failed"); }
  };
  const suspendTarget = async (uid) => {
    try { await api.post(`/admin/users/${uid}/suspend`); toast.success("User suspended"); load(); }
    catch { toast.error("Action failed"); }
  };

  return (
    <section data-testid="admin-reports">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <h2 className="font-serif text-4xl tracking-tight text-[#1A1818]">Reports.</h2>
        <div className="flex gap-2">
          {["open", "resolved", "all"].map((s) => (
            <button key={s} data-testid={`reports-filter-${s}`} onClick={() => setFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-full border ${
                filter === s ? "bg-[#1A1818] text-white border-[#1A1818]" : "border-[#E5DFD3] hover:bg-[#F0EBE1]"
              }`}>{s}</button>
          ))}
        </div>
      </div>
      {loading ? <Loader2 className="mt-8 animate-spin" /> : reports.length === 0 ? (
        <div className="mt-8 text-center text-[#737373] py-12" data-testid="reports-empty">
          <ShieldCheck size={28} strokeWidth={1.25} className="mx-auto mb-3 text-[#913831]" />
          <p className="font-serif text-2xl">All clear.</p>
          <p className="text-sm mt-1">No {filter !== "all" ? filter : ""} reports to review.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {reports.map((r) => (
            <div key={r.id} data-testid={`report-row-${r.id}`}
              className="bg-white border border-[#E5DFD3] rounded-2xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-[#F0EBE1] flex-shrink-0">
                  {r.target?.photos?.[0] ? <img src={resolvePhoto(r.target.photos[0])} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="font-medium text-[#1A1818]">{r.target?.name || "Unknown"} <span className="text-[#737373] text-xs">({r.target?.email})</span></div>
                    <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${
                      r.status === "open" ? "bg-[#FFF4F1] text-[#D9534F]" : "bg-[#F0EBE1] text-[#737373]"
                    }`}>{r.status}</span>
                  </div>
                  <div className="text-xs text-[#737373] mt-1">
                    Reported by {r.reporter?.name || "Unknown"} &middot; {new Date(r.created_at).toLocaleString()}
                  </div>
                  <div className="mt-3 text-sm text-[#1A1818] italic font-serif">&ldquo;{r.reason}&rdquo;</div>
                  {r.status === "open" && (
                    <div className="mt-4 flex gap-2 flex-wrap">
                      <button data-testid={`report-suspend-${r.id}`} onClick={() => suspendTarget(r.target_id)}
                        className="text-xs px-3 py-1.5 rounded-full bg-[#D9534F] hover:bg-[#B23B37] text-white"><Ban size={12} className="inline" /> Suspend user</button>
                      <button data-testid={`report-resolve-${r.id}`} onClick={() => resolve(r.id)}
                        className="text-xs px-3 py-1.5 rounded-full border border-[#E5DFD3] hover:bg-[#F0EBE1]"><Check size={12} className="inline" /> Mark resolved</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function VerificationsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/verifications").then(({ data }) => setRows(data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader2 className="animate-spin" />;
  return (
    <section data-testid="admin-verifications">
      <h2 className="font-serif text-4xl tracking-tight text-[#1A1818]">Verifications.</h2>
      <p className="text-sm text-[#737373] mt-1">Claude vision verdicts. Images are not stored — only the decision.</p>
      <div className="mt-6 space-y-2">
        {rows.map((v) => (
          <div key={v.id} data-testid={`verif-row-${v.id}`}
            className="bg-white border border-[#E5DFD3] rounded-2xl p-4 flex items-center gap-4">
            <span className={`w-10 h-10 rounded-full flex items-center justify-center ${
              v.approved ? "bg-[#F3E8DD] text-[#913831]" : "bg-[#FFF4F1] text-[#D9534F]"
            }`}>
              {v.approved ? <Check size={16} /> : <X size={16} />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#1A1818] truncate">
                {v.user?.name || v.user_id} <span className="text-[#737373] text-xs">({v.user?.email})</span>
              </div>
              <div className="text-xs text-[#737373] italic line-clamp-2 font-serif">&ldquo;{v.reason}&rdquo;</div>
            </div>
            <div className="text-xs text-right">
              <div className="text-[#737373]">{new Date(v.submitted_at).toLocaleString()}</div>
              <div className="uppercase tracking-[0.18em] mt-1">Conf: <span className="text-[#1A1818]">{v.confidence}</span></div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-[#737373] py-10 text-center" data-testid="verif-empty">No verification attempts yet.</p>}
      </div>
    </section>
  );
}

function TransactionsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/transactions").then(({ data }) => setRows(data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader2 className="animate-spin" />;
  const paid = rows.filter((r) => r.payment_status === "paid");
  const revenue = paid.reduce((s, r) => s + (r.amount || 0), 0);
  return (
    <section data-testid="admin-transactions">
      <h2 className="font-serif text-4xl tracking-tight text-[#1A1818]">Revenue.</h2>
      <div className="mt-5 inline-flex items-baseline gap-2 bg-[#1A1818] text-white rounded-2xl px-5 py-3" data-testid="revenue-total">
        <span className="text-xs uppercase tracking-[0.2em] text-[#D4A373]">Gross</span>
        <span className="font-serif text-3xl">${revenue.toFixed(2)}</span>
        <span className="text-xs text-white/60">/ {paid.length} paid</span>
      </div>
      <div className="mt-6 bg-white border border-[#E5DFD3] rounded-3xl overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#737373] border-b border-[#E5DFD3]">
          <div className="col-span-4">User</div>
          <div className="col-span-3">Package</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1">Date</div>
        </div>
        {rows.map((tx) => (
          <div key={tx.session_id} data-testid={`tx-row-${tx.session_id}`}
            className="grid grid-cols-12 gap-2 px-5 py-3 text-sm border-b border-[#E5DFD3]/60">
            <div className="col-span-4 truncate">{tx.user?.name || "—"} <span className="text-[#737373] text-xs">{tx.user?.email}</span></div>
            <div className="col-span-3 text-xs">{tx.package_id}</div>
            <div className="col-span-2">${tx.amount} {tx.currency?.toUpperCase()}</div>
            <div className={`col-span-2 text-xs ${tx.payment_status === "paid" ? "text-[#4caf50]" : "text-[#737373]"}`}>{tx.payment_status}</div>
            <div className="col-span-1 text-xs text-[#737373]">{new Date(tx.created_at).toLocaleDateString()}</div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-[#737373] py-10 text-center" data-testid="tx-empty">No transactions yet.</p>}
      </div>
    </section>
  );
}
